import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  CheckCircle2,
  History,
  Lock,
  Mic,
  Save,
  Scale,
  ShieldCheck,
} from 'lucide-react';
import api from '../services/api';
import { ErrorBox, SkeletonPage } from '../ui/kit';
import './ChatVoiceConfig.css';
import PageHeader from '../components/PageHeader';

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN-CHATVOICE-UI-001 — Bedienoberflaeche fuer den geschuetzten Owner-CAS-Vertrag
// (GET/PATCH /api/admin/chat-voice/config, backend/src/lib/chatVoicePolicy.js).
//
// Drei Eigenheiten des Vertrags bestimmen den Aufbau dieser Seite:
//   1. GENAU EIN Schluessel je Aufruf (`updates` wird mit 400 abgewiesen) → jede Karte hat ihren
//      eigenen Speicherknopf. Es gibt bewusst KEIN „Alles speichern", das bei Teilfehlern einen
//      unklaren Zustand hinterliesse.
//   2. `expected_revision` ist Pflicht (Compare-and-Set) → die Revision der letzten Antwort wird
//      mitgefuehrt. Bei 409 wird NICHT stillschweigend wiederholt: neu laden, Konflikt benennen,
//      zweite ausdrueckliche Bestaetigung abwarten.
//   3. `reason` ist Pflicht und landet im Audit → je Karte ein eigenes, LEERES Feld. Kein
//      vorausgefuellter Blindtext, der sich durchklicken liesse.
//
// Wertebereiche und erlaubte Werte kommen ausschliesslich aus `keys` (GATED_KEY_SPEC) der Antwort —
// nichts davon ist hier fest verdrahtet, sonst liefen Server und Oberflaeche auseinander.
// ─────────────────────────────────────────────────────────────────────────────

// Reihenfolge der Bearbeitung: zuerst die beiden offenen Rechtsentscheidungen, dann die Fristen,
// zuletzt der eigentliche Schalter. Jeder Schritt gelingt oder scheitert fuer sich.
const ORDER = [
  'chat_voice_evidence_retention_days',
  'chat_voice_account_deletion_policy',
  'retention_ride_chat_days',
  'retention_chat_days',
  'chat_voice_enabled',
];

const KEY_TEXT = {
  chat_voice_enabled: {
    eyebrow: 'Freischaltung',
    label: 'Sprachnachrichten freischalten',
    purpose: 'Der eigentliche Schalter für Sprachnachrichten, Zustellstatus, Meldefunktion und die '
      + 'automatische Löschung. Er wirkt erst, wenn alle Fristen gesetzt und beide Rechtsentscheidungen '
      + 'getroffen sind — vorher bleibt das Feature aus, auch wenn hier „An" steht.',
    undecided: 'Der Hauptschalter für Sprachnachrichten steht auf Aus.',
    invalid: 'Der hinterlegte Wert des Hauptschalters ist ungültig und gilt als Aus.',
  },
  retention_ride_chat_days: {
    eyebrow: 'Aufbewahrung',
    label: 'Aufbewahrung des Fahrt-Chats',
    purpose: 'So viele Tage nach dem belegbaren Fahrtende bleibt der Chat einer Fahrt erhalten. '
      + 'Danach wird er endgültig gelöscht.',
    undecided: 'Die Aufbewahrungsfrist des Fahrt-Chats ist noch nicht festgelegt.',
    invalid: 'Die hinterlegte Aufbewahrungsfrist des Fahrt-Chats ist ungültig.',
    unit: 'Tage',
  },
  retention_chat_days: {
    eyebrow: 'Aufbewahrung',
    label: 'Rückfallfrist für Chats ohne Fahrtende',
    purpose: 'Rückfalllösung für Chats, deren Fahrt nie einen Endzustand erreicht hat. '
      + 'Ohne diese Frist bliebe ein solcher Chat unbegrenzt liegen.',
    undecided: 'Die Rückfallfrist für Chats ohne belegbares Fahrtende ist noch nicht festgelegt.',
    invalid: 'Die hinterlegte Rückfallfrist ist ungültig.',
    unit: 'Tage',
  },
  chat_voice_evidence_retention_days: {
    eyebrow: 'Rechtsentscheidung',
    label: 'Aufbewahrung gemeldeter Inhalte',
    purpose: 'Höchstfrist in Tagen, die eine Beweiskopie eines gemeldeten Chatinhalts aufbewahrt '
      + 'werden darf. Das ist eine Datenschutzfrist — sie gehört von der Rechtsprüfung entschieden, '
      + 'nicht geschätzt.',
    undecided: 'Die Höchstfrist für gemeldete Inhalte ist noch nicht entschieden.',
    invalid: 'Die hinterlegte Höchstfrist für gemeldete Inhalte ist ungültig.',
    unit: 'Tage',
  },
  chat_voice_account_deletion_policy: {
    eyebrow: 'Rechtsentscheidung',
    label: 'Kontolöschung bei laufendem Fall',
    purpose: 'Was mit einer Beweiskopie geschieht, wenn ein beteiligtes Konto gelöscht wird, '
      + 'während der Fall noch offen ist. Die Auswahl beschreibt nur, was der Betrieb umsetzen kann — '
      + 'die Entscheidung selbst trifft die Rechtsprüfung.',
    undecided: 'Der Umgang mit Beweiskopien bei einer Kontolöschung ist noch nicht entschieden.',
    invalid: 'Die hinterlegte Regel zur Kontolöschung ist ungültig.',
  },
};

const VALUE_TEXT = {
  chat_voice_enabled: {
    0: 'Aus — Sprachnachrichten bleiben gesperrt',
    1: 'An — Freigabe erteilt (wirkt nur ohne offene Blocker)',
  },
  chat_voice_account_deletion_policy: {
    delete_evidence_with_account: 'Beweiskopie mit dem Konto löschen',
    anonymize_keep_case: 'Konto anonymisieren, Fallakte bis zur Klärung behalten',
    keep_until_case_resolved: 'Beweiskopie bis zum Fallabschluss behalten, danach löschen',
  },
};

const textFor = key => KEY_TEXT[key] || { eyebrow: 'Konfiguration', label: key, purpose: '' };
const valueLabel = (key, value) => (VALUE_TEXT[key] && VALUE_TEXT[key][value]) || String(value);

const UNDECIDED_CODES = ['config_missing', 'config_null', 'config_empty', 'config_undecided'];
const INVALID_CODES = ['config_not_integer', 'config_out_of_range', 'config_type', 'config_not_allowed'];
const REVISION_BROKEN = 'Der Änderungszähler ist beschädigt oder unlesbar — bis zur Klärung darf '
  + 'nichts geschrieben werden.';

// Blocker aus dem Gate-Zustand in einen deutschen Satz übersetzen. Unbekannte Codes werden roh
// gezeigt statt verschluckt — ein stiller Blocker wäre schlimmer als ein technischer Satz.
function blockerSentence(blocker) {
  const key = blocker && blocker.key;
  const code = blocker && blocker.code;
  const text = textFor(key);
  if (code === 'gate_off') return KEY_TEXT.chat_voice_enabled.undecided;
  if (code === 'gate_unreadable') return 'Der Hauptschalter konnte nicht gelesen werden.';
  if (code === 'revision_corrupt' || code === 'revision_unreadable') return REVISION_BROKEN;
  if (code === 'policy_not_enforceable') {
    return 'Die gewählte Regel zur Kontolöschung kann der Betrieb derzeit nicht umsetzen.';
  }
  if (code === 'config_unreadable') return `${text.label}: Der Wert konnte nicht gelesen werden.`;
  if (UNDECIDED_CODES.includes(code)) return text.undecided || `${text.label} ist noch nicht gesetzt.`;
  if (INVALID_CODES.includes(code)) return text.invalid || `${text.label} ist ungültig hinterlegt.`;
  return `${text.label}: ${code}`;
}

function limitText(spec) {
  if (!spec) return '';
  if (spec.type === 'integer') return `Erlaubt: ganze Zahl von ${spec.min} bis ${spec.max}.`;
  return `Erlaubt: ${(spec.allowed || []).join(' · ')}.`;
}

export default function ChatVoiceConfig() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [reasons, setReasons] = useState({});
  const [errors, setErrors] = useState({});
  const [conflicts, setConflicts] = useState({});
  const [busy, setBusy] = useState(null);

  // `resetDrafts` bewusst steuerbar: beim Nachladen nach einem Konflikt bleibt die Eingabe des
  // Owners stehen — sonst müsste er Wert und Begründung neu tippen, nur weil jemand anders schrieb.
  const load = useCallback(async ({ resetDrafts = false } = {}) => {
    const response = await api.get('/admin/chat-voice/config');
    const payload = response.data || {};
    setData(payload);
    if (resetDrafts) {
      const next = {};
      Object.keys(payload.keys || {}).forEach(key => {
        next[key] = payload.values && payload.values[key] != null ? String(payload.values[key]) : '';
      });
      setDrafts(next);
    }
    return payload;
  }, []);

  useEffect(() => {
    load({ resetDrafts: true })
      .catch(() => { setLoadError(true); toast.error('Chat-/Sprachkonfiguration konnte nicht geladen werden'); });
  }, [load]);

  if (loadError && !data) {
    return <ErrorBox text="Chat-/Sprachkonfiguration konnte nicht geladen werden." onRetry={() => window.location.reload()} />;
  }
  if (!data) return <SkeletonPage />;

  const spec = data.keys || {};
  const gate = data.gate || {};
  const revision = data.revision;
  const revisionBroken = revision == null || !!data.revision_error;
  const blockers = Array.isArray(gate.blockers) ? gate.blockers : [];
  const orderedKeys = [
    ...ORDER.filter(key => spec[key]),
    ...Object.keys(spec).filter(key => !ORDER.includes(key)),
  ];

  const setDraft = (key, value) => setDrafts(current => ({ ...current, [key]: value }));
  const setReason = (key, value) => setReasons(current => ({ ...current, [key]: value }));
  const setError = (key, value) => setErrors(current => ({ ...current, [key]: value }));

  const save = async key => {
    const reason = String(reasons[key] || '').trim();
    if (!reason) {
      setError(key, { reason: 'Begründung ist ein Pflichtfeld — sie wird ins Audit geschrieben.' });
      return;
    }
    const value = String(drafts[key] == null ? '' : drafts[key]).trim();
    if (!value) {
      setError(key, { value: 'Bitte zuerst einen Wert festlegen.' });
      return;
    }
    if (revisionBroken) {
      setError(key, { general: REVISION_BROKEN });
      return;
    }

    setError(key, null);
    setBusy(key);
    try {
      // GENAU EIN Schlüssel je Aufruf — exakt der Vertrag der Route.
      const response = await api.patch('/admin/chat-voice/config', {
        key,
        value,
        expected_revision: Number(revision),
        reason,
      });
      const result = response.data || {};
      setData(current => ({
        ...current,
        revision: result.revision,
        revision_error: null,
        gate: result.gate || current.gate,
        values: { ...(current.values || {}), [key]: value },
      }));
      setReason(key, '');                                   // keine Wiederverwendung der Begründung
      setConflicts(current => ({ ...current, [key]: null }));
      toast.success(`Gespeichert · Revision ${result.revision}`);
    } catch (error) {
      const status = error?.response?.status;
      const payload = error?.response?.data || {};
      if (status === 409) {
        // Compare-and-Set fehlgeschlagen: NICHT wiederholen. Neu laden, benennen, erneut bestätigen
        // lassen — sonst überschriebe ein blinder Retry die fremde Änderung.
        const detail = (payload.fields && payload.fields.expected_revision) || '';
        try { await load(); } catch (_) { /* Anzeige bleibt beim letzten bekannten Stand */ }
        setConflicts(current => ({ ...current, [key]: detail }));
        setError(key, null);
        toast.error('Revisionskonflikt — Stand neu geladen, bitte erneut bestätigen');
        return;
      }
      const fields = payload.fields && Object.keys(payload.fields).length ? payload.fields : null;
      setError(key, fields || { general: payload.error || 'Speichern fehlgeschlagen' });
      toast.error(payload.error || 'Speichern fehlgeschlagen');
    } finally {
      setBusy(null);
    }
  };

  const active = !!gate.enabled;
  const verdictHint = active
    ? 'Alle Voraussetzungen sind erfüllt. Die hinterlegten Fristen laufen.'
    : gate.status === 'pending'
      ? 'Es fehlt nur noch eine Rechtsentscheidung — technisch ist alles bereit.'
      : 'Es sind noch technische Voraussetzungen offen.';

  return (
    <div className="cv-page">
      <PageHeader
        title="Chat &amp; Sprache"
        description="Sprachnachrichten, Meldefunktion und die automatische Löschung sind ausgeliefert, aber fail-closed: ohne ausdrückliche Konfiguration bleibt alles aus. Jeder Wert wird einzeln gespeichert, mit Begründung protokolliert und gegen die aktuelle Revision geprüft."
        actions={
        <div className="cv-hero-badges" aria-label="Geltungsbereich">
          <span><Lock size={14} /> Nur Owner · nicht delegierbar</span>
          <span data-testid="cv-revision">
            <History size={14} /> Revision {revision == null ? '–' : revision}
          </span>
        </div>
      }
      />

      <section className={`cv-verdict ${active ? 'is-active' : ''}`} data-testid="cv-verdict">
        <div className="cv-verdict-icon">{active ? <CheckCircle2 size={22} /> : <Mic size={22} />}</div>
        <div>
          <strong>{active ? 'Sprachnachrichten sind aktiv.' : 'Sprachnachrichten sind ausgeschaltet.'}</strong>
          <p>{verdictHint}</p>
        </div>
        <span className={`cv-verdict-status ${active ? 'is-active' : ''}`}>{active ? 'Aktiv' : 'Aus'}</span>
      </section>

      {!active && blockers.length > 0 && (
        <section className="cv-blockers" data-testid="cv-blockers" aria-label="Offene Voraussetzungen">
          <div className="cv-blockers-head"><AlertTriangle size={18} /> Das fehlt noch</div>
          <ul>
            {blockers.map((blocker, index) => (
              <li key={`${blocker.key}-${blocker.code}-${index}`}>{blockerSentence(blocker)}</li>
            ))}
          </ul>
        </section>
      )}

      {revisionBroken && (
        <section className="cv-alarm"><AlertTriangle size={18} /> {REVISION_BROKEN}</section>
      )}

      <div className="cv-cards">
        {orderedKeys.map((key, index) => {
          const keySpec = spec[key] || {};
          const text = textFor(key);
          const stored = data.values ? data.values[key] : null;
          const draft = drafts[key] == null ? '' : drafts[key];
          const fieldErrors = errors[key] || {};
          const conflict = conflicts[key];
          const reason = reasons[key] || '';
          const legal = !!keySpec.legal_decision;
          const dirty = String(stored == null ? '' : stored) !== String(draft);
          return (
            <article
              className={`cv-card ${legal ? 'is-legal' : ''} ${conflict ? 'has-conflict' : ''}`}
              key={key}
              data-config-key={key}
            >
              <div className="cv-card-head">
                <span className="cv-step">{String(index + 1).padStart(2, '0')}</span>
                <div className="cv-card-title">
                  <span>{legal ? <><Scale size={12} /> {text.eyebrow}</> : text.eyebrow}</span>
                  <h2>{text.label}</h2>
                </div>
                <span className={`cv-state ${stored == null ? 'is-unset' : 'is-set'}`}>
                  {stored == null ? 'Noch nicht gesetzt' : `Aktuell: ${valueLabel(key, stored)}${text.unit ? ` ${text.unit}` : ''}`}
                </span>
              </div>

              <p className="cv-purpose">{text.purpose}</p>
              <p className="cv-spec">
                {limitText(keySpec)}
                <small>{keySpec.description}</small>
              </p>

              <div className={`cv-inputs ${keySpec.type === 'enum' ? 'is-enum' : ''}`}>
                <label className="cv-field">
                  <span>Neuer Wert {text.unit ? <small>{text.unit}</small> : null}</span>
                  {keySpec.type === 'integer' ? (
                    <input
                      type="number"
                      data-field="value"
                      min={keySpec.min}
                      max={keySpec.max}
                      step="1"
                      value={draft}
                      onChange={event => setDraft(key, event.target.value)}
                      aria-label={`${text.label} — neuer Wert`}
                    />
                  ) : (
                    <select
                      data-field="value"
                      value={draft}
                      onChange={event => setDraft(key, event.target.value)}
                      aria-label={`${text.label} — neuer Wert`}
                    >
                      {/* Solange nichts gewaehlt ist, darf die Auswahl keinen Wert VORTAEUSCHEN:
                          ohne Platzhalter zeigte der Browser den ersten Eintrag an, obwohl nichts
                          gesetzt ist — bei einer Rechtsentscheidung waere das eine Lüge. */}
                      {draft === '' && <option value="" disabled>— noch nicht entschieden —</option>}
                      {(keySpec.allowed || []).map(option => (
                        <option key={option} value={option}>{valueLabel(key, option)}</option>
                      ))}
                    </select>
                  )}
                  {fieldErrors.value && <em className="cv-error">{fieldErrors.value}</em>}
                </label>

                <label className="cv-field">
                  <span>Begründung <small>Pflicht · max. 200 Zeichen · geht ins Audit</small></span>
                  <textarea
                    data-field="reason"
                    value={reason}
                    maxLength={200}
                    rows={2}
                    onChange={event => setReason(key, event.target.value)}
                    placeholder="Wer hat das entschieden und warum?"
                    aria-label={`${text.label} — Begründung`}
                  />
                  {fieldErrors.reason && <em className="cv-error">{fieldErrors.reason}</em>}
                </label>
              </div>

              {fieldErrors.general && <div className="cv-error-box">{fieldErrors.general}</div>}
              {Object.entries(fieldErrors)
                .filter(([name]) => !['value', 'reason', 'general'].includes(name))
                .map(([name, message]) => <div className="cv-error-box" key={name}>{message}</div>)}

              {conflict && (
                <div className="cv-conflict">
                  Die Konfiguration wurde zwischenzeitlich geändert ({conflict}). Der Stand wurde neu
                  geladen — prüfe den Wert und bestätige die Änderung ausdrücklich erneut.
                </div>
              )}

              <div className="cv-card-foot">
                <span className="cv-dirty">
                  {stored == null && !String(draft).trim()
                    ? 'Noch nichts eingetragen'
                    : dirty
                      ? 'Nicht gespeicherter Entwurf'
                      : 'Entwurf entspricht dem gespeicherten Wert'}
                </span>
                <button
                  type="button"
                  data-action="save"
                  onClick={() => save(key)}
                  disabled={busy === key}
                  aria-label={`${text.label} speichern`}
                >
                  <Save size={16} />
                  {busy === key
                    ? 'Speichert…'
                    : conflict
                      ? 'Erneut bestätigen und speichern'
                      : 'Diesen Wert speichern'}
                </button>
              </div>
            </article>
          );
        })}
      </div>

      <footer className="cv-note">
        <ShieldCheck size={18} />
        <span>
          <strong>Ein Wert je Speichervorgang.</strong>
          <small>
            Die Route nimmt bewusst genau einen Schlüssel entgegen: So ist jede Änderung einzeln
            begründet, einzeln protokolliert und einzeln nachvollziehbar. Ein Sammel-Speichern gibt
            es nicht.
          </small>
        </span>
      </footer>
    </div>
  );
}
